import { useState, useEffect, createContext, useContext, type ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, nombre?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        toast({
          title: "Error de autenticación",
          description: error.message,
          variant: "destructive",
        });
      } else {
        // Obtener el nombre del usuario desde el perfil o metadata
        let userName = data.user?.user_metadata?.nombre || data.user?.email?.split('@')[0] || 'Usuario';
        
        // Si no hay nombre en metadata, intentar obtenerlo del perfil
        if (!data.user?.user_metadata?.nombre) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('nombre')
            .eq('user_id', data.user.id)
            .single();
          
          if (profileData?.nombre) {
            userName = profileData.nombre;
          }
        }
        
        toast({
          title: `¡Bienvenido, ${userName}!`,
          description: "Inicio de sesión exitoso",
          duration: 3000, // 3 segundos
        });
      }
      
      return { error };
    } catch (error: any) {
      toast({
        title: "Error de autenticación",
        description: "Ocurrió un error inesperado",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signUp = async (email: string, password: string, nombre?: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            nombre: nombre || ''
          }
        }
      });
      
      if (error) {
        toast({
          title: "Error de registro",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "¡Registro exitoso!",
          description: "Revisa tu email para confirmar tu cuenta",
        });
      }
      
      return { error };
    } catch (error: any) {
      toast({
        title: "Error de registro",
        description: "Ocurrió un error inesperado",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      // Limpiar estado persistido al cerrar sesión
      const userId = user?.id;
      if (userId) {
        // Limpiar localStorage de datos del usuario
        Object.keys(localStorage).forEach(key => {
          if (key.includes(userId)) {
            localStorage.removeItem(key);
          }
        });
      }
      
      // Limpiar todos los estados relacionados
      localStorage.removeItem('modal_state');
      localStorage.removeItem('viabiliza_last_route');
      localStorage.removeItem('viabiliza_modal_state');
      
      // Intentar cerrar sesión en el servidor
      const { error } = await supabase.auth.signOut();
      
      // Limpiar estado local siempre, incluso si hay error en el servidor
      setUser(null);
      setSession(null);
      
      // Solo mostrar error si no es un error de sesión no encontrada
      if (error && !error.message.includes('session_not_found')) {
        toast({
          title: "Advertencia",
          description: "Sesión cerrada localmente",
        });
      } else {
        toast({
          title: "Sesión cerrada",
          description: "Has cerrado sesión correctamente",
        });
      }
    } catch (error) {
      console.error('Error signing out:', error);
      // Limpiar estado local de todas formas
      setUser(null);
      setSession(null);
      toast({
        title: "Sesión cerrada",
        description: "Sesión cerrada localmente",
      });
    }
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};